import type { SessionSummary } from '@league/shared/contract';
import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '../../lib/api';
import { DirectorLifecycleBadge } from './DirectorLifecycleBadge';
import { LeagueTabs, type TABS } from './LeagueTabs';
import type { League } from './LeagueWorkspace';
import { useDirectorLifecycle } from './useDirectorLifecycle';

/**
 * THE TOP OF EVERY PAGE IN A LEAGUE — the league's name and status, its one-line summary, and the
 * tabs. The league's own tabs and its sessions' score sheet and seating board all
 * draw this, so they look the same and the tabs never move (owner, 2026-10-15: "The tabs and back
 * are not consistent").
 *
 * NO "← ALL LEAGUES" (owner, 2026-10-15). The sidebar's My Leagues is always there and already
 * marked; a second way to the same place was one more thing to read past on every page. Leaving with
 * unsaved work is still caught — the tabs ask, through `onLeave`.
 *
 * The league is read through the same queries the workspace uses, so moving between a tab and a
 * session's page costs no extra request. On a session page the league's name is not the page's
 * heading — the page has its own ("Session 2 scores") — so `pageHeading` is off there.
 */
/**
 * THE TWO READS A LEAGUE'S PAGES SHARE — declared once, here, and used by the workspace too, so the
 * shapes are written down a single time (the contract ratchet counts every hand-written one).
 */
export const fetchLeague = (leagueId: string) =>
  apiFetch<{ league: League; sessions: SessionSummary[] }>(`/api/leagues/${leagueId}`);
/* Moved here unchanged from the workspace; not yet in the contract (follow-up: type `directorLeagues`). */
export const fetchDirectorLeagues = () =>
  apiFetch<{ leagues: {
    id: string;
    lifecycleSessions: { startsAt: string; status: string; hasSavedSeating: boolean }[];
  }[] }>('/api/director/leagues');

export function LeagueHeader({
  leagueId,
  current,
  onLeave,
  pageHeading = true,
}: {
  leagueId: string;
  current?: (typeof TABS)[number]['label'] | undefined;
  onLeave?: (() => boolean) | undefined;
  pageHeading?: boolean;
}) {
  const query = useQuery({
    queryKey: ['league', leagueId],
    queryFn: () => fetchLeague(leagueId),
  });
  const directorLeagues = useQuery({
    queryKey: ['director-leagues'],
    queryFn: fetchDirectorLeagues,
  });
  /* Never fatal: the header is decoration on the page under it, and a missing list must not blank it. */
  const directorLeague = directorLeagues.data?.leagues?.find((league) => league.id === leagueId);
  const [lifecycle] = useDirectorLifecycle(
    query.data?.league && directorLeague
      ? [{ status: query.data.league.status, sessions: directorLeague.lifecycleSessions }]
      : [],
  );

  const league = query.data?.league;
  const sessions = query.data?.sessions ?? [];
  const Name = pageHeading ? 'h1' : 'p';

  return (
    <>
      {/* Identity, status, and a one-line summary. UI-UX-SPEC §3.3: "above the tabs". */}
      <header className="flex min-h-[4.25rem] flex-col gap-1">
        {league ? (
          <>
            <div className="flex flex-wrap items-baseline gap-3">
              {/* A long unbroken word in a league name wraps rather than widening the page on a narrow phone. */}
              <Name className="min-w-0 font-display text-3xl leading-tight [overflow-wrap:anywhere]">{league.name}</Name>
              {lifecycle ? <DirectorLifecycleBadge lifecycle={lifecycle} /> : null}
            </div>
            <p className="text-[14px] text-ink-2">
              {league.venueName ? `${league.venueName} · ` : ''}
              {sessions.length} session{sessions.length === 1 ? '' : 's'} · Capacity: {league.playerCap}
            </p>
          </>
        ) : null}
      </header>

      <LeagueTabs leagueId={leagueId} current={current} onLeave={onLeave} className="-mx-6 mt-6 px-6" />
    </>
  );
}

/**
 * A page inside a league that is not one of its tabs — a session's score sheet or seating board —
 * laid out exactly as the league's own pages are: the same width, the same header, the content under
 * the tabs at the same distance. So the tabs sit in one place on every page of a league.
 */
export function LeaguePage({
  children,
  ...header
}: Parameters<typeof LeagueHeader>[0] & { children: React.ReactNode }) {
  return (
    <div className="mx-auto flex max-w-4xl flex-col px-6 py-10">
      <LeagueHeader {...header} pageHeading={false} />
      <div className="mt-8">{children}</div>
    </div>
  );
}
